// Import Swiper styles
import { forwardRef, useCallback, useMemo, useState } from 'react'
import React from 'react'
import { ChevronLeft } from '@mui/icons-material'
import NotListedLocationIcon from '@mui/icons-material/NotListedLocation'
import {
  Box,
  Button,
  Dialog,
  Grid,
  Slide,
  SlideProps,
  Stack,
  SxProps,
  Theme
} from '@mui/material'
import SpaceApartBox from 'common/Box/SpaceApartBox'
import NormalText from 'common/Typography/NormalText'
import { FreeMode, Mousewheel, Scrollbar } from 'swiper'
import { Swiper, SwiperSlide } from 'swiper/react'

import 'swiper/css/pagination'

import 'swiper/css'

interface PreferredNetworkType {
  countryName: string
  countryCode: string
  countryFlag: string
  networks: Array<string>
}

interface Props {
  preferredNetworks: Array<PreferredNetworkType>
  children?: React.ReactNode
  showCountriesOnly?: boolean
  sx?: SxProps<Theme>
  planName?: string
  dialogHeader?: string
}

interface CountryType {
  name: string
  iso2: string
  iso3: string
  flag: string
  label: string
}

const Transition = forwardRef(function Transition(
  props: SlideProps,
  ref: React.Ref<unknown>
) {
    return <Slide direction='left' ref={ref} {...props} />
  })

export type { CountryType }

export default function NetworksList({
  preferredNetworks,
  showCountriesOnly = false,
  children,
  dialogHeader,
  planName
}: Props) {
  const [isModalOpen, setModalOpen] = useState(false)
  const [searchNetwork, setSearchNetwork] = useState<CountryType | null>(null)

  const sortedPreferredNetworks = useMemo(
    () =>
      preferredNetworks.sort((a, b) =>
        a.countryName.toLowerCase().localeCompare(b.countryName.toLowerCase())
      ),
    [preferredNetworks]
  )

  const handleClose = useCallback(() => {
    setModalOpen(false)
    setSearchNetwork(null)
  }, [])

  const NetworkComponent = useMemo(() => {
    let curr = ''
    const component: Array<React.ReactNode> = []
    let filteredNetworks = sortedPreferredNetworks
    if (searchNetwork) {
      filteredNetworks = preferredNetworks?.filter(
        (x) => x.countryCode === searchNetwork.iso3
      )
    }
    let temp: Array<React.ReactNode> = []
    filteredNetworks?.forEach((network, idx) => {
      const t = network.countryName.charAt(0)
      if (t !== curr) {
        if (temp.length > 0) {
          component.push(
            <SwiperSlide key={`${idx}-slider`}>{temp}</SwiperSlide>
          )
        }
        temp = [
          <Box mt={2} mx={2} key={`${idx}-${t}`}>
            <NormalText text={t} theme='muted' />
          </Box>
        ]
        curr = network.countryName.charAt(0)
      }

      temp.push(
        <Box m={2} key={`${idx}-${network?.countryName}`}>
          <SpaceApartBox
            sx={{
              alignItems: 'start',
              borderBottom: '1px solid #E4E7EB'
            }}
          >
            <Stack
              direction='row'
              spacing={1}
              display='flex'
              sx={{
                alignItems: 'center'
              }}
            >
              <div
                style={{
                  filter: 'drop-shadow(0 0 0.2rem lightgray)'
                }}
              >
                {/*
                <PictureWrapper
                  image={network?.countryFlag}
                  style={{
                    height: '18px',
                    width: '18px'
                  }}
                />*/}
              </div>
              <NormalText text={network?.countryName} isBold />
            </Stack>
            {!showCountriesOnly && (
              <Stack>
                {network?.networks.map((telco, idx2) => (
                  <React.Fragment key={`${telco}-${idx}-${idx2}`}>
                    {<NormalText text={telco} textAlign='right' />}
                  </React.Fragment>
                ))}
              </Stack>
            )}
          </SpaceApartBox>
        </Box>
      )
    })
    if (temp.length > 0) {
      component.push(<SwiperSlide key={`last-slider`}>{temp}</SwiperSlide>)
    }
    if (temp.length < 1) {
      component.push(
        <SwiperSlide key={`no-countries-slider`}>
          <Grid container columnGap={2} sx={{ m: '1rem' }}>
            <Grid item xs={2}>
              <Box
                sx={{
                  backgroundColor: '#E4E7EB',
                  width: '42px',
                  height: '42px',
                  borderRadius: '8px',
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center'
                }}
              >
                <NotListedLocationIcon sx={{ color: '#0015C4' }} />
              </Box>
            </Grid>
            <Grid item sx={{ alignSelf: 'center' }} xs={8}>
              <NormalText theme='dark'>
                Sorry, the country you’re looking for isn’t covered in this data
                pack
              </NormalText>
            </Grid>
          </Grid>
        </SwiperSlide>
      )
    }

    return component
  }, [sortedPreferredNetworks, searchNetwork, showCountriesOnly])
  return (
    <Box>
      <Box onClick={() => setModalOpen(!isModalOpen)} zIndex={999}>
        {children}
      </Box>
      <Dialog
        TransitionComponent={Transition}
        keepMounted
        open={isModalOpen}
        onClose={handleClose}
        aria-labelledby='modal-modal-title'
        aria-describedby='modal-modal-description'
        fullWidth
        fullScreen
        sx={{
          '& .MuiDialog-container': {
            '& .MuiPaper-root': {
              width: '40%',
              maxWidth: '40%',
              margin: 0,
              height: '100%',
              position: 'fixed',
              bottom: 0,
              right: 0,
              borderRadius: '24px 0 0 0',
              overflowY: 'hidden'
            }
          }
        }}
      >
        <SpaceApartBox
          sx={{
            borderBottom: '1px solid rgba(0,0,0,0.1)',
            height: 'max-content',
            backgroundColor: '#DDF7F9',
            position: 'relative'
          }}
        >
          <Stack sx={{ padding: '1rem' }}>
            <Button
              variant='noBackground'
              onClick={handleClose}
              sx={{
                width: 'fit-content',
                height: 'fit-content',
                color: '#0000FF',
                pt: '1vh',
                justifyContent: 'left'
              }}
            >
              <ChevronLeft
                sx={{
                  color: '#333333'
                }}
              />
            </Button>
            <NormalText
              isBold
              theme='dark'
              fontSize='1.5rem'
              sx={{ px: '1vw' }}
            >
              {planName}
            </NormalText>
          </Stack>
        </SpaceApartBox>
        <Box>
          <Stack mx={2}>
            <NormalText
              theme='blue'
              text={dialogHeader}
              isBold
              fontSize='1.25rem'
              textAlign='left'
              sx={{ px: '1vw' }}
            />

            <SpaceApartBox>
              {!showCountriesOnly && (
                <NormalText
                  text='Country'
                  isBold
                  variant='body1'
                  theme='blue'
                />
              )}
              {!showCountriesOnly && (
                <NormalText
                  text='Preferred Networks'
                  theme='blue'
                  variant='body1'
                />
              )}
            </SpaceApartBox>
          </Stack>
        </Box>
        <Swiper
          direction={'vertical'}
          slidesPerView={'auto'}
          freeMode={true}
          mousewheel={true}
          className='networkSwiper'
          modules={[FreeMode, Scrollbar, Mousewheel]}
        >
          {NetworkComponent}
        </Swiper>
        <Box
          sx={{
            position: 'absolute',
            right: '1vw',
            top: '30%'
          }}
        ></Box>
      </Dialog>
    </Box>
  )
}
